import axios from 'axios';

// Use relative URL for both development and production since we have proxy configured
const API_URL = ''; // Use relative URL - proxy will handle forwarding to backend

const API_KEY = process.env.REACT_APP_API_KEY || 'generate-a-secure-random-key-here';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
  },
  timeout: 60000, // 60 second timeout (increased from 30)
});

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timeout - the server took too long to respond');
    }
    if (!error.response) {
      throw new Error('Network error - please check your connection');
    }
    throw error;
  }
);

export interface User {
  id: string;
  client_id: string;
  email: string;
  name?: string;
  forwarding_domain?: string;
  platform: string;
  workspace_id?: string;
  status?: string;
  num_domains?: number;
  domain_count?: number;
  pending_domains?: number;
  earliest_unverified_date?: string;
  delivery_status?: 'on_track' | 'due_approaching' | 'overdue' | 'unknown';
  admin_notes?: string;
  client_notes?: string;
  assigned_admin?: string;
  on_track?: string;
  order_status?: string;
  created_at: string;
  updated_at?: string;
}

export interface Domain {
  id: string;
  domain_name: string;
  domain_password?: string;
  admin_password?: string;
  otp_secret?: string;
  account_type: string;
  platform: string;
  registrar?: string;
  emails?: string;
  names?: string;
  accounts_to_create?: string;
  accounts_to_cancel?: string;
  verified_connection: boolean;
  completed_upload: boolean;
  dkim_added: boolean;
  site_verification_record_added?: string;
  finished?: string;
  created_at: string;
  bubble_creation_date?: string;
  canceled_date?: string;
  canceled_domain?: boolean;
  forwarding_domain?: string;
  redirect_url_link?: string;
  bubble_workspace_id?: string;
  bubble_client_id?: string;
  verify_result_1?: string;
  verify_result_2?: string;
  verify_result_3?: string;
  verify_result_4?: string;
  verify_result_5?: string;
  created_zone?: string;
  last_cloudflare_status_check?: string;
  assigned_va?: string;
  claimed_at?: string;
}

export interface UsersResponse {
  data: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UserDetailsResponse {
  success: boolean;
  data: User;
}

export interface DomainsResponse {
  data: Domain[];
}

export interface ReconnectRequest {
  id: string;
  external_id: string;
  email: string;
  password?: string;
  domain: string;
  workspace_id: string;
  platform: string;
  priority: 'urgent' | 'high' | 'normal' | 'low';
  status: string;
  is_reconnected: boolean;
  attempts: number;
  error_message?: string;
  created_at: string;
  updated_at: string;
  reconnected_at?: string;
}

export interface ReconnectStatistics {
  total_requests: number;
  unique_workspaces: number;
  completed_requests: number;
  pending_requests: number;
  processing_requests: number;
  failed_requests: number;
  cancelled_requests: number;
  disabled_requests: number;
  edu_suspended_requests: number;
  tfa_sms_requests: number;
  incorrect_password_requests: number;
  captcha_requests: number;
  google_not_found_requests: number;
  spam_requests: number;
}

export interface UserStatistics {
  total_clients: number;
  unique_workspaces: number;
  pending_domains: number;
  new_domains_24h: number;
}

export interface ReconnectRequestsResponse {
  success: boolean;
  data: ReconnectRequest[];
  grouped_data?: GroupedReconnectRequests[];
  statistics: ReconnectStatistics;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface GroupedReconnectRequests {
  workspace_id: string;
  requests: ReconnectRequest[];
  count: number;
}

export interface CanceledSmartleadAccount {
  id: string;
  domain_name: string;
  accounts_to_create?: string;
  accounts_to_cancel?: string;
  canceled_date?: string;
  created_at: string;
  bubble_client_id?: string;
  client_email?: string;
  client_name?: string;
}

export interface CanceledSmartleadResponse {
  success: boolean;
  data: CanceledSmartleadAccount[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateReconnectRequest {
  email: string;
  priority?: 'urgent' | 'high' | 'normal' | 'low';
  id?: string; // Optional custom external ID for the request
  workspace_id?: string; // Optional workspace ID (if provided with password, skips domain lookup)
  password?: string; // Optional password (if provided with workspace_id, skips domain lookup)
}

export const apiService = {
  getUnpurchasedDomains: async (sinceIso?: string): Promise<{
    success: boolean;
    data: Array<{ id: string; domain_name: string; created_domain?: string | null; created_at?: string; bubble_creation_date?: string }>;
    count: number;
    message: string;
  }> => {
    const params: any = {};
    if (sinceIso) params.since = sinceIso;
    const response = await api.get('/api/domains/unpurchased-orders', { params });
    return response.data;
  },

  markDomainsOrdered: async (domains: string[]): Promise<{
    success: boolean;
    updated: number;
    data: Array<{ id: string; domain_name: string; created_domain: string }>;
    message: string;
  }> => {
    const response = await api.post('/api/domains/mark-ordered', { domains });
    return response.data;
  },
  getUsers: async (params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    platform?: string;
    order_status?: string;
    delivery_status?: string;
    domain_creation_date?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<UsersResponse> => {
    const response = await api.get('/api/users', { params });
    return response.data;
  },

  getUser: async (userId: string): Promise<UserDetailsResponse> => {
    const response = await api.get(`/api/users/${userId}`);
    return response.data;
  },

  getUserDomains: async (userId: string): Promise<DomainsResponse> => {
    const response = await api.get(`/api/users/${userId}/domains`);
    return response.data;
  },

  getDomainByName: async (domainName: string): Promise<Domain | null> => {
    try {
      const response = await api.get(`/api/domains/by-name/${domainName}`);
      return response.data.success ? response.data.data : null;
    } catch (error) {
      console.error('Error fetching domain by name:', error);
      return null;
    }
  },

  createZone: async (domainName: string): Promise<{
    success: boolean;
    domain: string;
    response?: any;
    message: string;
    error?: string;
  }> => {
    try {
      const response = await api.post('/api/domains/create-zone', {
        domainName: domainName
      });
      return response.data;
    } catch (error) {
      console.error('Error creating zone:', error);
      return {
        success: false,
        domain: domainName,
        message: 'Failed to create zone',
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  getReconnectRequests: async (params: {
    page?: number;
    limit?: number;
    status?: string;
    priority?: string;
    search?: string;
    dateFilter?: string;
    groupBy?: string;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  } = {}): Promise<ReconnectRequestsResponse> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        queryParams.append(key, value.toString());
      }
    });
    
    const response = await api.get('/api/reconnect-requests', { params: queryParams });
    return response.data;
  },

  getReconnectStatistics: async (params: {
    status?: string;
    priority?: string;
    dateFilter?: string;
    search?: string;
  } = {}): Promise<{
    success: boolean;
    statistics: ReconnectStatistics;
    cached?: boolean;
    stale?: boolean;
    error?: boolean;
  }> => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
      
      const response = await api.get('/api/reconnect-requests/stats', { params: queryParams });
      return response.data;
    } catch (error) {
      // Return default statistics on error
      return {
        success: false,
        statistics: {
          total_requests: 0,
          unique_workspaces: 0,
          completed_requests: 0,
          pending_requests: 0,
          processing_requests: 0,
          failed_requests: 0,
          cancelled_requests: 0,
          disabled_requests: 0,
          edu_suspended_requests: 0,
          tfa_sms_requests: 0,
          incorrect_password_requests: 0,
          captcha_requests: 0,
          google_not_found_requests: 0,
          spam_requests: 0,
        },
        error: true
      };
    }
  },

  createReconnectRequest: async (data: CreateReconnectRequest): Promise<{
    success: boolean;
    data: ReconnectRequest;
    message: string;
  }> => {
    const response = await api.post('/api/reconnect-requests', data);
    return response.data;
  },

  updateReconnectRequestStatus: async (
    requestId: string,
    status: string,
    errorMessage?: string
  ): Promise<{
    success: boolean;
    data: ReconnectRequest;
    message: string;
  }> => {
    const response = await api.put(`/api/reconnect-requests/${requestId}/status`, {
      status,
      error_message: errorMessage,
    });
    return response.data;
  },

  processReconnectRequest: async (
    requestId: string
  ): Promise<{
    success: boolean;
    data: ReconnectRequest;
    message: string;
  }> => {
    const response = await api.post(`/api/reconnect-requests/${requestId}/process`);
    return response.data;
  },

  getUserStatistics: async (platform?: string): Promise<{
    success: boolean;
    statistics: UserStatistics;
  }> => {
    try {
      const params: any = {};
      if (platform) params.platform = platform;
      const response = await api.get('/api/users/stats', { params });
      return response.data;
    } catch (error) {
      // Return default statistics on error
      return {
        success: false,
        statistics: {
          total_clients: 0,
          unique_workspaces: 0,
          pending_domains: 0,
          new_domains_24h: 0,
        }
      };
    }
  },

  updateUserOrderStatus: async (
    userId: string,
    orderStatus: string
  ): Promise<{
    success: boolean;
    data: User;
    message: string;
  }> => {
    const response = await api.patch(`/api/users/${userId}/order-status`, {
      order_status: orderStatus,
    });
    return response.data;
  },

  updateUserClientNotes: async (
    userId: string,
    clientNotes: string
  ): Promise<{
    success: boolean;
    data: User;
    message: string;
  }> => {
    const response = await api.patch(`/api/users/${userId}/notes`, {
      client_notes: clientNotes,
    });
    return response.data;
  },

  updateUserAllNotes: async (
    userId: string,
    clientNotes: string,
    adminNotes: string
  ): Promise<{
    success: boolean;
    data: User;
    message: string;
  }> => {
    const response = await api.patch(`/api/users/${userId}/all-notes`, {
      client_notes: clientNotes,
      admin_notes: adminNotes,
    });
    return response.data;
  },

  getDomainStatus: async (domainName: string): Promise<{
    success: boolean;
    status: string;
    message?: string;
  }> => {
    try {
      const response = await api.post('/api/domains/check-status', {
        domains: [domainName]
      });
      
      if (response.data.success && response.data.results && response.data.results.length > 0) {
        const result = response.data.results[0];
        return {
          success: result.success,
          status: result.success ? (result.status || 'Pending') : 'Pending',
          message: result.message || response.data.message
        };
      }
      
      return {
        success: false,
        status: 'Pending',
        message: response.data.message || 'Failed to get domain status'
      };
    } catch (error) {
      console.error('Error getting domain status:', error);
      return {
        success: false,
        status: 'Pending',
        message: 'Failed to get domain status'
      };
    }
  },

  updateDomainStatus: async (
    domainId: string,
    status: string
  ): Promise<{
    success: boolean;
    data?: any;
    message: string;
  }> => {
    try {
      const response = await api.patch(`/api/domains/${domainId}/cloudflare-status`, {
        status: status
      });
      return response.data;
    } catch (error) {
      console.error('Error updating domain status:', error);
      return {
        success: false,
        message: 'Failed to update domain status'
      };
    }
  },

  // Helper function to check if we should make a Cloudflare status API call
  shouldCheckCloudflareStatus: (domain: Domain): boolean => {
    // If verified_connection is true, no need to check
    if (domain.verified_connection) {
      return false;
    }

    // If we already have Active status, no need to check
    if (domain.created_zone === 'Active') {
      return false;
    }

    // If never checked, we should check
    if (!domain.last_cloudflare_status_check) {
      return true;
    }

    // Check if last check was more than 30 minutes ago
    const lastCheck = new Date(domain.last_cloudflare_status_check);
    const thirtyMinutesAgo = new Date(Date.now() - (30 * 60 * 1000));
    
    return lastCheck < thirtyMinutesAgo;
  },

  // Add DKIM record
  addDkimRecord: async (domainName: string, content: string): Promise<{
    success: boolean;
    message: string;
    response?: any;
  }> => {
    try {
      const response = await api.post('/api/domains/add-dkim', {
        domainName,
        content
      });
      return response.data;
    } catch (error) {
      console.error('Error adding DKIM record:', error);
      return {
        success: false,
        message: 'Failed to add DKIM record'
      };
    }
  },

  // Add Google Site Verification record
  addGoogleVerification: async (domainName: string, content: string): Promise<{
    success: boolean;
    message: string;
    response?: any;
  }> => {
    try {
      const response = await api.post('/api/domains/add-google-verification', {
        domainName,
        content
      });
      return response.data;
    } catch (error) {
      console.error('Error adding Google Site Verification record:', error);
      return {
        success: false,
        message: 'Failed to add Google Site Verification record'
      };
    }
  },

  // Verify connections for pending domains
  verifyPendingDomains: async (userId: string): Promise<{
    success: boolean;
    message: string;
    results?: any[];
    summary?: {
      total: number;
      processed: number;
      successful: number;
      failed: number;
    };
  }> => {
    try {
      const response = await api.post('/api/domains/verify-pending', {
        userId
      });
      return response.data;
    } catch (error) {
      console.error('Error verifying pending domains:', error);
      return {
        success: false,
        message: 'Failed to verify pending domains'
      };
    }
  },

  // Verify a single domain
  verifySingleDomain: async (domainId: string): Promise<{
    success: boolean;
    domain: string;
    totalEmails: number;
    connectedEmails: number;
    allEmailsConnected: boolean;
    results: Array<{
      email: string;
      success: boolean;
      connected: boolean;
      response?: any;
      error?: string;
    }>;
    message: string;
  }> => {
    try {
      const response = await api.put(`/api/domains/${domainId}/verify`);
      return response.data;
    } catch (error) {
      console.error('Error verifying single domain:', error);
      return {
        success: false,
        domain: 'Unknown',
        totalEmails: 0,
        connectedEmails: 0,
        allEmailsConnected: false,
        results: [],
        message: 'Failed to verify domain'
      };
    }
  },

  // Background job management
  createVerificationJob: async (userId: string, domainIds: string[] = [], priority: number = 5): Promise<{
    success: boolean;
    data?: any;
    message: string;
  }> => {
    try {
      const response = await api.post('/api/jobs/verification', {
        userId,
        domainIds,
        priority
      });
      return response.data;
    } catch (error) {
      console.error('Error creating verification job:', error);
      return {
        success: false,
        message: 'Failed to create verification job'
      };
    }
  },

  createOAuthUploadJob: async (userId: string, domainIds: string[] = [], priority: number = 5): Promise<{
    success: boolean;
    data?: any;
    message: string;
  }> => {
    try {
      const response = await api.post('/api/jobs/oauth-upload', {
        userId,
        domainIds,
        priority
      });
      return response.data;
    } catch (error) {
      console.error('Error creating oAuth upload job:', error);
      return {
        success: false,
        message: 'Failed to create oAuth upload job'
      };
    }
  },

  getJobStatus: async (jobId: string): Promise<{
    success: boolean;
    data?: any;
    message?: string;
  }> => {
    try {
      const response = await api.get(`/api/jobs/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting job status:', error);
      return {
        success: false,
        message: 'Failed to get job status'
      };
    }
  },

  getUserJobs: async (userId: string, limit: number = 10, status?: string): Promise<{
    success: boolean;
    data?: any[];
    count?: number;
    message?: string;
  }> => {
    try {
      const params: any = { limit };
      if (status) params.status = status;
      
      const response = await api.get(`/api/jobs/user/${userId}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error getting user jobs:', error);
      return {
        success: false,
        message: 'Failed to get user jobs'
      };
    }
  },

  // Send password to domain emails
  sendDomainPassword: async (domainId: string): Promise<{
    success: boolean;
    domain: string;
    totalEmails: number;
    successCount: number;
    failCount: number;
    results: Array<{
      email: string;
      success: boolean;
      status?: number;
      error?: string;
    }>;
    message: string;
  }> => {
    try {
      const response = await api.put(`/api/domains/${domainId}/send-password`);
      return response.data;
    } catch (error) {
      console.error('Error sending domain password:', error);
      return {
        success: false,
        domain: 'Unknown',
        totalEmails: 0,
        successCount: 0,
        failCount: 0,
        results: [],
        message: 'Failed to send domain password'
      };
    }
  },

  // Send passwords to all pending domains for a user
  sendPasswordsPendingDomains: async (userId: string): Promise<{
    success: boolean;
    message: string;
    results?: any[];
    summary?: {
      total: number;
      processed: number;
      successful: number;
      failed: number;
    };
  }> => {
    try {
      const response = await api.post('/api/domains/send-passwords-pending', {
        userId
      });
      return response.data;
    } catch (error) {
      console.error('Error sending passwords for pending domains:', error);
      return {
        success: false,
        message: 'Failed to send passwords for pending domains'
      };
    }
  },

  // Direct oAuth Upload for individual domain
  oAuthUploadDomain: async (domainId: string): Promise<{
    success: boolean;
    domain: string;
    totalEmails: number;
    successCount: number;
    failCount: number;
    skippedCount?: number;
    emailResults: Array<{
      email: string;
      success?: boolean;
      status?: number;
      error?: string;
      reason?: string;
      verifyResult?: string;
    }>;
    message: string;
  }> => {
    try {
      // Use PUT request since the endpoint doesn't need a request body
      const response = await api.put(`/api/domains/${domainId}/oauth-upload`);
      return response.data;
    } catch (error) {
      console.error('Error in oAuth upload:', error);
      
      // Capture more detailed error information
      if (error instanceof Error && 'response' in error) {
        const axiosError = error as any;
        const errorMessage = axiosError.response?.data?.error || 
                           axiosError.response?.data?.details || 
                           axiosError.response?.data?.message || 
                           axiosError.message;
        const statusCode = axiosError.response?.status;
        
        console.error('oAuth upload error details:', {
          status: statusCode,
          error: errorMessage,
          responseData: axiosError.response?.data
        });
        
        return {
          success: false,
          domain: 'Unknown',
          totalEmails: 0,
          successCount: 0,
          failCount: 0,
          skippedCount: 0,
          emailResults: [],
          message: `HTTP ${statusCode}: ${errorMessage}`
        };
      }
      
      return {
        success: false,
        domain: 'Unknown',
        totalEmails: 0,
        successCount: 0,
        failCount: 0,
        skippedCount: 0,
        emailResults: [],
        message: error instanceof Error ? error.message : 'Failed to upload oAuth credentials'
      };
    }
  },

  // Claim a domain for a manager
  claimDomain: async (domainId: string, managerUsername: string): Promise<{
    success: boolean;
    data?: Domain;
    message: string;
  }> => {
    try {
      const response = await api.patch(`/api/domains/${domainId}/claim`, {
        manager_username: managerUsername
      });
      return response.data;
    } catch (error) {
      console.error('Error claiming domain:', error);
      return {
        success: false,
        message: 'Failed to claim domain'
      };
    }
  },

  // Unclaim a domain (remove assignment)
  unclaimDomain: async (domainId: string): Promise<{
    success: boolean;
    data?: Domain;
    message: string;
  }> => {
    try {
      const response = await api.patch(`/api/domains/${domainId}/unclaim`);
      return response.data;
    } catch (error) {
      console.error('Error unclaiming domain:', error);
      return {
        success: false,
        message: 'Failed to unclaim domain'
      };
    }
  },

  // Get manager by username (for display name)
  getManagerByUsername: async (username: string): Promise<{
    success: boolean;
    data?: {
      username: string;
      firstName: string;
      lastName: string;
      userType: string;
    };
    message?: string;
  }> => {
    try {
      const response = await api.get(`/api/managers/${username}`);
      return response.data;
    } catch (error) {
      console.error('Error getting manager:', error);
      return {
        success: false,
        message: 'Failed to get manager details'
      };
    }
  },

  getDomainsByDateRange: async (beginDate: string, endDate: string): Promise<{
    success: boolean;
    data: Domain[];
    count: number;
    smartlead_in_range?: number;
    message: string;
  }> => {
    try {
      const response = await api.get('/api/domains/by-date-range', {
        params: { beginDate, endDate }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching domains by date range:', error);
      return {
        success: false,
        data: [],
        count: 0,
        message: 'Failed to fetch domains'
      };
    }
  },

  getClientsByDateRange: async (beginDate: string, endDate: string): Promise<{
    success: boolean;
    data: User[];
    count: number;
    message: string;
  }> => {
    try {
      const response = await api.get('/api/clients/by-date-range', {
        params: { beginDate, endDate }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching clients by date range:', error);
      return {
        success: false,
        data: [],
        count: 0,
        message: 'Failed to fetch clients'
      };
    }
  },
  
  // Prewarmed domains inventory
  getPrewarmedDomains: async (params: {
    page?: number;
    limit?: number;
    status?: 'available' | 'ordered';
    search?: string;
  } = {}): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      domain_name: string;
      email_account: string;
      first_name?: string;
      last_name?: string;
      status: 'available' | 'ordered';
      ordered_at?: string;
      ordered_by?: string;
      vendor_order_id?: string;
      created_at: string;
      updated_at: string;
    }>;
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> => {
    const response = await api.get('/api/prewarmed-domains', { params });
    return response.data;
  },

  createPrewarmedDomain: async (item: {
    domain_name: string;
    email_account: string;
    first_name?: string;
    last_name?: string;
  }): Promise<{
    success: boolean;
    data: any;
  }> => {
    const response = await api.post('/api/prewarmed-domains', item);
    return response.data;
  },

  bulkCreatePrewarmedDomains: async (items: Array<{
    domain_name: string;
    email_account: string;
    first_name?: string;
    last_name?: string;
  }>): Promise<{
    success: boolean;
    data: any[];
    count: number;
  }> => {
    const response = await api.post('/api/prewarmed-domains/bulk', { items });
    return response.data;
  },

  orderPrewarmedDomain: async (
    id: string,
    ordered_by?: string,
    vendor_order_id?: string
  ): Promise<{
    success: boolean;
    data: any;
  }> => {
    const response = await api.post(`/api/prewarmed-domains/${id}/order`, { ordered_by, vendor_order_id });
    return response.data;
  },

  // Smartlead canceled accounts
  getSmartleadCancels: async (params: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  } = {}): Promise<CanceledSmartleadResponse> => {
    const response = await api.get('/api/domains/smartlead-cancels', { params });
    return response.data;
  },
};

export default apiService; 